/* eslint-disable react/destructuring-assignment */
import { useState, useEffect, useMemo } from 'react';
import { useTable, usePagination, useBlockLayout, Column, useSortBy } from 'react-table';
import { useLocation } from 'react-router';
import cn from 'classnames';

import {
  AppListRecord,
  ApplicationType,
  AppStatusEnum,
  OrganizationType,
  PlanEnum,
} from 'api-bible-shared-lib/utils/types';
import { usePlans } from '../contexts/Plans';
import Container from '../components/Container';
import { Table, Pagination } from '../components/Table';
import Card from '../components/Card';
import { SuperAdminHeader } from '../components/SuperAdminHeader';
import ApplicationEditModal from '../components/ApplicationEditModal';

import { toLocaleDateTimeString } from '../utils/time';
import * as portalApi from '../utils/api';

export default function SuperAdminApplications() {
  const { plansById } = usePlans();
  const [loadedApps, setLoadedApps] = useState(false);
  const [apps, setApps] = useState<AppListRecord[]>([]);
  const [error, setError] = useState<Error | void>();

  // plan url state
  const location = useLocation();
  const searchPlans = new URLSearchParams(location.search).get('plan') || '';
  const selectedPlans = useMemo(() => searchPlans.split(',').filter((x) => !!x), [searchPlans]);

  // search / filter apps state
  const [query, setQuery] = useState('');
  const [appPlanFilter, setAppPlanFilter] = useState<PlanEnum | ''>('');
  const [appStatusFilter, setAppStatusFilter] = useState<AppStatusEnum | ''>('');
  const [appTypeFilter, setAppTypeFilter] = useState<ApplicationType | ''>('');
  const [orgTypeFilter, setOrgTypeFilter] = useState<OrganizationType | ''>('');

  const [modalInput, setModalInput] = useState<AppListRecord | undefined>(undefined);
  const [filteredBibles, setFilteredApps] = useState<AppListRecord[]>([]);

  // handles fetching initial list of apps
  useEffect(() => {
    portalApi
      .getSuperAdminApps()
      .then((allApps) => {
        setApps(allApps);
        setFilteredApps(allApps);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
        setError(err);
      })
      .finally(() => setLoadedApps(true));
  }, []);

  // handles search results
  useEffect(() => {
    if (!apps.length) return;

    const sanitizedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const searchRegex = new RegExp(`\\b${sanitizedQuery}`, 'i');

    setFilteredApps(
      apps.filter((app: AppListRecord) => {
        const matchesAppPlan = appPlanFilter ? app.appPlan === appPlanFilter : true;
        const matchesAppStatus = appStatusFilter ? app.appStatus === appStatusFilter : true;
        const matchesAppType = appTypeFilter ? app.appType === appTypeFilter : true;
        const matchesOrgType = orgTypeFilter ? app.accountType === orgTypeFilter : true;

        const matchesSearch =
          !query ||
          searchRegex.test(app.appName) ||
          searchRegex.test(app.appApiKey) ||
          searchRegex.test(app.appId) ||
          searchRegex.test(app.accountName) ||
          searchRegex.test(app.accountId) ||
          searchRegex.test(app.userName) ||
          searchRegex.test(app.userEmail);

        return (
          matchesAppType && matchesOrgType && matchesSearch && matchesAppPlan && matchesAppStatus
        );
      }),
    );
  }, [apps, query, appPlanFilter, appStatusFilter, appTypeFilter, orgTypeFilter, selectedPlans]);

  const columns = useMemo<Column<AppListRecord>[]>(
    () => [
      { accessor: 'appPlan', Header: 'Plan', width: 100 },
      { accessor: 'appName', Header: 'Application', width: 200 },
      { accessor: 'accountName', Header: 'Account', width: 200 },
      { accessor: 'userName', Header: 'User', width: 200 },
      { accessor: 'appStatus', Header: 'Status', width: 100 },
      {
        accessor: (row) => row.apiKeyCounts?.dailyCount,
        Header: 'Daily',
        Cell: (props: any) => (
          <div className="text-right">
            {props.value ? Number(props.value).toLocaleString() : ''}
          </div>
        ),
        width: 80,
      },
      {
        accessor: (row) => row.apiKeyCounts?.monthlyCount,
        Header: 'Monthly',
        Cell: (props: any) => (
          <div className="text-right">
            {props.value ? Number(props.value).toLocaleString() : ''}
          </div>
        ),
        width: 80,
      },
      {
        accessor: (row) => row.apiKeyCounts?.countUpdatedAt,
        Header: 'Last Activity At',
        Cell: (props: any) => (props.value ? toLocaleDateTimeString(props.value) : ''),
        width: 180,
      },
      {
        accessor: 'appCreatedAt',
        Header: 'Created At',
        Cell: (props: any) => (props.value ? toLocaleDateTimeString(props.value) : ''),
        width: 180,
      },
    ],
    [],
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    rows,
    state: { pageIndex },
  } = useTable(
    {
      initialState: { pageSize: 12 },
      columns,
      data: filteredBibles,
    },
    useSortBy,
    usePagination,
    useBlockLayout,
  );

  const loadedAll = loadedApps && Object.keys(plansById).length > 0;

  return (
    <Container>
      <SuperAdminHeader />
      <Card className="relative px-6 py-8 mb-8 overflow-x-auto md:py-12 md:px-10">
        {!loadedAll && 'Please wait while loading all apps. This may take up to 10 seconds...'}

        {loadedAll && (
          <div className="flex flex-wrap items-end px-2 mb-4 md:flex-nowrap">
            <div className="w-full pr-3">
              <div className="relative mt-1 rounded-md">
                <div className="absolute inset-y-0 left-0 flex items-center">
                  <label htmlFor="appPlan" className="sr-only">
                    Plan
                  </label>
                  <select
                    id="appPlan"
                    name="appPlan"
                    className="h-full py-0 pl-3 text-gray-500 bg-transparent border-transparent focus:ring-blue-500 focus:border-blue-500 pr-7 sm:text-sm rounded-md"
                    onChange={(e) => setAppPlanFilter(e.target.value as PlanEnum)}
                  >
                    <option value="">All Plans</option>
                    {Object.keys(plansById).map((planId) => (
                      <option key={planId} value={planId}>
                        {planId}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="absolute inset-y-0 left-24 flex items-center">
                  <label htmlFor="appType" className="sr-only">
                    App Type
                  </label>
                  <select
                    id="appType"
                    name="appType"
                    className="h-full py-0 pl-3 text-gray-500 bg-transparent border-transparent focus:ring-blue-500 focus:border-blue-500 pr-7 sm:text-sm rounded-md"
                    onChange={(e) => setAppTypeFilter(e.target.value as ApplicationType)}
                  >
                    <option value="">All App Types</option>
                    {Object.values(ApplicationType).map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="absolute inset-y-0 left-60 flex items-center">
                  <label htmlFor="orgType" className="sr-only">
                    Org Type
                  </label>
                  <select
                    id="orgType"
                    name="orgType"
                    className="h-full py-0 pl-3 text-gray-500 bg-transparent border-transparent focus:ring-blue-500 focus:border-blue-500 pr-7 sm:text-sm rounded-md"
                    onChange={(e) => setOrgTypeFilter(e.target.value as OrganizationType)}
                  >
                    <option value="">All Org Types</option>
                    {Object.values(OrganizationType).map((value) => (
                      <option key={value} value={value}>
                        {value.replace(/Organization|Developer/gi, (match) => match.slice(0, 3))}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="absolute inset-y-0 left-96 flex items-center">
                  <label htmlFor="appType" className="sr-only">
                    Status
                  </label>
                  <select
                    id="appStatus"
                    name="appStatus"
                    className="h-full py-0 pl-3 text-gray-500 bg-transparent border-transparent focus:ring-blue-500 focus:border-blue-500 pr-7 sm:text-sm rounded-md"
                    onChange={(e) => setAppStatusFilter(e.target.value as AppStatusEnum)}
                  >
                    <option value="">All Statuses</option>
                    {Object.values(AppStatusEnum).map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
                <input
                  type="text"
                  name="search"
                  id="search"
                  className="block w-full pl-128 border-gray-300 focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  placeholder="Search app name or id or key, account name or id, or user name or email"
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
              <div className="mt-6 text-sm text-gray-500">
                Click on the column name to sort, and click on the row to see details.
              </div>
            </div>
          </div>
        )}

        {loadedAll && error && (
          <h3 className="text-lg text-center text-red-500">Error loading apps</h3>
        )}

        {loadedAll && !error && (
          <>
            <Table {...getTableProps()} className="w-full mb-6">
              <thead className="text-xs text-gray-400 uppercase">
                {headerGroups.map((headerGroup) => {
                  const headerGroupProps = headerGroup.getHeaderGroupProps();
                  return (
                    <tr
                      {...headerGroupProps}
                      className={cn(headerGroupProps.className, 'flex')}
                      style={{ width: '100%' }}
                    >
                      {headerGroup.headers.map((column, index) => {
                        const headerProps = column.getHeaderProps(column.getSortByToggleProps());
                        const style = index === 1 ? { width: '100%' } : {};
                        return (
                          <th
                            {...headerProps}
                            className={cn(
                              headerProps.className,
                              'p-3 font-normal font-semibold tracking-wider text-left',
                              {
                                'flex-shrink-0 flex-grow-0': index !== 1,
                              },
                            )}
                            style={{ ...headerProps.style, ...style, minWidth: 'unset' }}
                          >
                            {column.render('Header')}
                            <span>
                              {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                            </span>
                          </th>
                        );
                      })}
                    </tr>
                  );
                })}
              </thead>
              <tbody
                className="w-full text-gray-900 transition-all ease duration-100"
                {...getTableBodyProps()}
              >
                {page.map((row) => {
                  prepareRow(row);
                  const rowProps = row.getRowProps();
                  return (
                    <tr
                      {...rowProps}
                      className={cn(
                        rowProps.className,
                        'flex py-2 rounded-sm hover:bg-gray-100 transition-all duration-100 mb-.5 items-center overflow-hidden rounded-lg',
                      )}
                      style={{ ...rowProps.style, width: '100%' }}
                      onClick={() => setModalInput(row.original)}
                    >
                      {row.cells.map((cell, index) => {
                        const cellProps = cell.getCellProps();
                        const style =
                          index !== 1 ? cellProps.style : { ...cellProps.style, width: '100%' };
                        return (
                          <td
                            {...cellProps}
                            className={cn(
                              cellProps.className,
                              'px-3 py-2 whitespace-nowrap text-left truncate',
                              {
                                'flex items-center': index === 0,
                                'flex-shrink-0 flex-grow-0': index !== 1,
                              },
                            )}
                            style={style}
                          >
                            {cell.render('Cell')}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {page.length === 0 && (
                  <tr className="py-2 rounded-sm hover:bg-gray-100 transition-all duration-100 mb-.5 items-center">
                    <td className="w-full px-3 py-2 text-center text-gray-500">
                      No matching results.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
            <Pagination
              className="flex justify-center"
              pageCount={pageCount}
              pageIndex={pageIndex}
              gotoPage={gotoPage}
              canPreviousPage={canPreviousPage}
              canNextPage={canNextPage}
              previousPage={previousPage}
              nextPage={nextPage}
            />
            <div className="mt-6 text-sm text-center text-gray-500">
              {query ? `${rows.length} matching apps found` : `${rows.length} apps`}
            </div>
          </>
        )}
      </Card>

      {modalInput && (
        <ApplicationEditModal
          appListRecord={modalInput}
          handleCancel={() => setModalInput(undefined)}
        />
      )}
    </Container>
  );
}
